import { useState, useEffect } from 'react';
import { useModalA11y } from '../hooks/use-modal-a11y';

interface RejectionReasonModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (reason: string) => void;
  lineItemName?: string;
  isSubmitting?: boolean;
  isBulkDecline?: boolean;
}

export function RejectionReasonModal({ 
  isOpen, 
  onClose, 
  onSubmit, 
  lineItemName,
  isSubmitting = false,
  isBulkDecline = false
}: RejectionReasonModalProps) {
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setReason('');
      setError('');
    }
  }, [isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate required field
    if (!reason.trim()) {
      setError('Please provide a reason for rejection');
      return;
    }

    // Submit the reason
    onSubmit(reason.trim());
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  const { dialogRef } = useModalA11y<HTMLDivElement>(isOpen, handleClose);

  if (!isOpen) return null;

  return (
    <div data-testid="rejection-reason-modal" className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div 
        className={`fixed inset-0 bg-black bg-opacity-50 transition-opacity ${
          isSubmitting ? 'cursor-wait' : 'cursor-default'
        }`}
        onClick={handleClose}
      />
      
      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="rejection-reason-modal-title"
          tabIndex={-1}
          className="relative bg-white dark:bg-secondary-800 rounded-lg shadow-xl max-w-md w-full p-6 focus:outline-none"
        >
          {/* Loading Overlay */}
          {isSubmitting && (
            <div className="absolute inset-0 bg-white dark:bg-secondary-800 bg-opacity-75 rounded-lg flex items-center justify-center z-10">
              <div className="text-center">
                <i className="fa-solid fa-spinner fa-spin text-3xl text-red-600 dark:text-red-400 mb-2"></i>
                <p className="text-sm text-gray-600 dark:text-secondary-400">
                  {isBulkDecline ? 'Processing decline...' : 'Processing rejection...'}
                </p>
              </div>
            </div>
          )}
          
          {/* Header */}
          <div className="mb-4">
            <h3 id="rejection-reason-modal-title" className="text-lg font-semibold text-gray-900 dark:text-white">
              {isBulkDecline ? 'Decline All Items' : 'Reject Line Item'}
            </h3>
            {lineItemName && !isBulkDecline && (
              <p className="mt-1 text-sm text-gray-600 dark:text-secondary-400">
                {lineItemName}
              </p>
            )}
            {isBulkDecline && (
              <p className="mt-1 text-sm text-gray-600 dark:text-secondary-400">
                You are about to decline all pending items in this order.
              </p>
            )}
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label htmlFor="rejectionReason" className="block text-sm font-medium text-gray-700 dark:text-secondary-200 mb-2">
                Reason for Rejection <span className="text-red-600 dark:text-red-400">*</span>
              </label>
              <textarea
                data-testid="rejection-reason-input"
                id="rejectionReason"
                rows={4}
                className={`w-full px-3 py-2 border rounded-md bg-white dark:bg-secondary-900 text-gray-900 dark:text-secondary-100 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  error ? 'border-red-500' : 'border-gray-300 dark:border-secondary-600'
                }`}
                placeholder={isBulkDecline 
                  ? "Please explain why you are declining all items..."
                  : "Please explain why you are rejecting this item..."}
                value={reason}
                onChange={(e) => {
                  setReason(e.target.value);
                  setError(''); // Clear error on input
                }}
                disabled={isSubmitting}
                autoFocus
              />
              {error && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{error}</p>
              )}
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-3">
              <button
                data-testid="rejection-reason-cancel"
                type="button"
                onClick={handleClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-secondary-200 bg-white dark:bg-secondary-800 border border-gray-300 dark:border-secondary-600 rounded-md hover:bg-gray-50 dark:hover:bg-secondary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                data-testid="rejection-reason-submit"
                type="submit"
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <span className="flex items-center">
                    <i className="fa-solid fa-spinner fa-spin mr-2"></i>
                    {isBulkDecline ? 'Declining...' : 'Rejecting...'}
                  </span>
                ) : (
                  isBulkDecline ? 'Decline All Items' : 'Reject Item'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
